import React, { useEffect, useMemo, useState } from 'react'
import config from '../../config'
import { post } from '../../utils/http'
import { SessionBrief } from '../../types/SessionBrief'
import { Link, useParams } from 'react-router-dom'
import SpinnerIcon from '../Icons/SpinnerIcon'
import Filter from '../SessionFilter/SessionFilter'
import SessionInfo from '../SessionInfo/SessionInfo'
import { useNavigate } from 'react-router-dom'
import { getFilterPayload, getNumberRangeErrors, useFilterContext } from '../../contexts/filter-context'
import SessionListItem from '../SessionListItem/SessionListItem'
import RefreshIcon from '../Icons/RefreshIcon'
import { useDeviceContext } from '../../contexts/device-context'
import { useAlertContext } from '../../contexts/alert-context'
import { useHistoryContext } from '../../contexts/history-context'
import { IcaDialog } from '../IcaElements/IcaDialog'
import { IcaHeader } from '../IcaElements/IcaHeader'
import { IcaSortingDropDown } from '../IcaElements/IcaSortingDropDown'
import { SortingChangedEvent } from '../Picklist/picklistHelpers'
import { sortByDate } from '../../utils/sortUtils'
import { IcaInputSearch } from '../IcaElements/IcaInputSearch'
import { IcaAlertMessage } from '../IcaElements/IcaAlertMessage'
import { getCountryCode } from '../../utils/userUtils'
import SearchPlaceholder from '../SearchPlaceholder/SearchPlaceholder'

export default function SessionsList() {
  const navigate = useNavigate()
  const { sessionId } = useParams()
  const { filterState } = useFilterContext()
  const { deviceState } = useDeviceContext()
  const { alert } = useAlertContext()
  const { historyState, historyReducer } = useHistoryContext()
  const [sessions, setSessions] = useState<Array<SessionBrief>>([])
  const [loading, setLoading] = useState(true)
  const [selectedCustomerId, setSelectedCustomerId] = React.useState<string | null>(null)
  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const hasDoneInitialScroll = React.useRef<boolean>(false)
  const [search, setSearch] = useState<string>('')

  const [sortingOptions, setSortingOptions] = useState([
    { id: 'start-asc', value: 'Starttid stigande', selected: false },
    { id: 'start-desc', value: 'Starttid fallande', selected: false },
    { id: 'end-asc', value: 'Sluttid stigande', selected: false },
    { id: 'end-desc', value: 'Sluttid fallande', selected: true },
  ])

  const selectedSession = useMemo(() => sessions.find(s => s.id === sessionId), [sessions, sessionId])

  const filteredSessions = useMemo(() => {
    let result = sessions
    if (filterState.filter.unviewedSessions) {
      result = result.filter(session => !historyReducer.hasViewedSession(session.id))
    }

    if (search) {
      const query = search.toLowerCase().trim()
      result = result.filter(session =>
        session.customerId.toLowerCase().startsWith(query) ||
        session.id.toLowerCase().startsWith(query) || 
        session.totalPrice.toFixed(2).startsWith(query)
      )
    }

    const selectedSortingOption = sortingOptions.find(option => option.selected)?.id
    if (selectedSortingOption) {
      switch (selectedSortingOption) {
        case 'start-asc':
          return sortByDate(result, session => new Date(session.firstActivity), 'asc')
        case 'start-desc':
          return sortByDate(result, session => new Date(session.firstActivity), 'desc')
        case 'end-asc':
          return sortByDate(result, session => new Date(session.latestActivity), 'asc')
        case 'end-desc':
          return sortByDate(result, session => new Date(session.latestActivity), 'desc')
        default:
          return result
      }
    }

    return result
  }, [sessions, filterState.filter.unviewedSessions, historyState.viewedSessions, sortingOptions, search])

  const fetchSessions = async (onSuccessMessage?: string) => {
    const rangeErrors = getNumberRangeErrors(filterState.filter)
    const filter = filterState.filter

    if (
      !filter.storeId ||
      filter.from > filter.to ||
      Object.values(rangeErrors).some(Boolean)
    ) {
      setLoading(false)
      return
    }

    setLoading(true)
    setSessions([])
    try {
      const filterPayload = getFilterPayload(filterState.filter)
      const sessions = await post<typeof filterPayload, Array<SessionBrief>>(`${config.STORE_API_BASE_URL}/v2/session/`, filterPayload)
      setSessions(sessions)
      if (onSuccessMessage) {
        alert.success(onSuccessMessage)
      }

      updateSessionPath(sessions)
    } catch {
      alert.error('Någonting gick fel.')
    } finally {
      setLoading(false)
    }
  }

  const handleShowCustomer = () => {
    if (selectedSession) {
      navigate(`/customers/${selectedSession.customerId}/${getCountryCode(selectedSession.countryCode, selectedSession.acr)}${selectedSession.acr ? `?acr=${selectedSession.acr}` : ''}`, {
        state: { previousUrl: location.pathname }
      })
    }
  }

  useEffect(() => {
    fetchSessions()
    setSelectedCustomerId(null)
    hasDoneInitialScroll.current = false    
  }, [filterState.filter.storeId,
    filterState.filter.from,
    filterState.filter.to,
    filterState.filter.paymentMissing,
    filterState.filter.removedItems,
    filterState.filter.accessDeniedBanned,
    filterState.filter.accessDeniedTermsNotMet,
    filterState.filter.multiplePayments,
    filterState.filter.flagged,
    filterState.filter.minItemCount,
    filterState.filter.maxItemCount,
    filterState.filter.minSessionDurationMinutes,
    filterState.filter.maxSessionDurationMinutes,
    filterState.filter.minPriceKr,
    filterState.filter.maxPriceKr,
    filterState.filter.minCustomerWeighedItemGrams,
    filterState.filter.maxCustomerWeighedItemGrams,
    filterState.filter.excludeEmptySessionsNearPurchase])

  useEffect(() => {
    if (sessions.length > 0 && !hasDoneInitialScroll.current && scrollRef.current) {
      const top = scrollRef.current.getBoundingClientRect().top + window.scrollY
      const offset = deviceState.isMobile ? 96 : 70 // Header + 4px
      window.scrollTo({
        top: top - offset,
        behavior: 'smooth'
      })

      hasDoneInitialScroll.current = true
    }
  }, [sessions.length, deviceState.isMobile])

  const onModalClose = () => {
    navigate('/sessions')
  }

  const handleSortingChange = (e: SortingChangedEvent) => {
    const newOptions = e.detail
    setSortingOptions(newOptions)
  }

  const handleSearchChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.currentTarget.value)
  }

  const handleSearchClear = () => {
    setSearch('')
  }

  const handleSessionFlagChanged = (sessionId: string, isFlagged: boolean, customerId: string, countryCode: string) => {
    if (filterState.filter.flagged && !isFlagged && !selectedSession?.isCustomerFlagged) {
      const updatedSessions = sessions.filter(session => session.id !== sessionId)
      setSessions(updatedSessions)
      updateSessionPath(updatedSessions)
    } else {
      setSessions(prevSessions => prevSessions.map(session =>
        session.id === sessionId
          ? {
            ...session,
            isSessionFlagged: isFlagged,
            isCustomerFlagged: isFlagged || session.isCustomerFlagged
          } : {
            ...session,
            isCustomerFlagged: isFlagged && session.customerId === customerId && getCountryCode(session.countryCode, session.acr) === countryCode
              ? true : session.isCustomerFlagged
          }
      ))
    }
  }

  const updateSessionPath = (sessions: SessionBrief[]) => {
    if (sessionId && !sessions.find(session => session.id === sessionId)) {
      navigate('/sessions')
    }
  }

  return (
    <div className='list-content__container'>
      <div className={`list list-content__left ${deviceState.isMobile ? '-mt-1' : ''}`}>
        <div className={`flex ${deviceState.isMobile ? 'mb-4 items-end' : 'mb-2'}`}>
          <div className={`list-content__count flex ${deviceState.isMobile ? 'flex-col gap-1' : ''}`}>
            <span className='list-content__count-label mr-3'>Visar {filteredSessions.length} kundvarv</span>
            <RefreshIcon size='16px' fontSize='12px' color='var(--color-accent-red)' onClick={() => fetchSessions('Kundvarv hämtade.')} text='Uppdatera'></RefreshIcon>
          </div>
          <div className={`ml-auto ${deviceState.isMobile ? '' : 'w-[180px]'}`}>
            <IcaSortingDropDown
              options={JSON.stringify(sortingOptions)}
              position="right"
              onSortingchanged={handleSortingChange}
            />
          </div>
        </div>
        {deviceState.isMobile && (
          <div className="mb-2" >
            <IcaAlertMessage persistencetype="persistent" alarmtype="info" prominencetype="subtle" text="Du kan markera ett kundvarv för att hitta kundvarv som har samma kund-id." />
          </div>
        )}
        {loading &&
          <div className='grid place-content-center'>
            <SpinnerIcon />
          </div>
        }
        {filteredSessions.map((session) => {
          const isScrollTarget = sessionId === session.id
          const selected = selectedCustomerId === session.customerId

          return (
            <div
              className={`list-item ${selected ? 'list-item__selected' : ''}`}
              key={session.id}
              ref={isScrollTarget ? scrollRef : null}
            >
              <Link to={`/sessions/${session.id}`} onClick={() => historyReducer.addViewedSession(session.id)}>
                <SessionListItem
                  session={session}
                  setSelectedCustomerId={setSelectedCustomerId}
                  selectedCustomerId={selectedCustomerId}
                  viewed={historyReducer.hasViewedSession(session.id)}
                  pageType='session'
                />
              </Link>
            </div>
          )
        })}
        {!loading && filteredSessions.length === 0 && <SearchPlaceholder backgroundColor="var(--color-white)" />}
      </div>
      <div className='list-content__right'>
        <div className={`${deviceState.isMobile ? '-mt-3 mb-2' : 'absolute -top-15 w-full'}`}>
          <IcaInputSearch
            label='Belopp, kund-id eller kundvarvs-id'
            value={search}
            onInput={handleSearchChanged}
            onClear={handleSearchClear}
          />
        </div>

        <Filter />
      </div>

      {sessionId && selectedSession &&
      <>
        <IcaDialog
          open={!!sessionId}
          onClose={onModalClose}
          snaptotop
        >
          <IcaHeader
            slot='header'
            variant='dialog'
            heading='Kundvarv'
            leftaction='close'
            onLeftactionclick={onModalClose}
            actions={JSON.stringify([{ icon: 'send', label: 'Visa kund', id: 'action' }])}
            onRightactionclick={handleShowCustomer}
          />
          <SessionInfo onSessionFlagChanged={(sessionId, isFlagged, customerId, countryCode) => handleSessionFlagChanged(sessionId, isFlagged, customerId, countryCode)} />
        </IcaDialog>
      </>
      }
    </div>
  )
}

